import { cloneDeep, flow, forEach, has, keys } from 'es-toolkit/compat';

import messagesEN from '../../features/translations/en-GB.json';
import { localeData } from '../../features/translations/localeData';
import { getDisplayLocale } from './DisplayLocaleService';
import { DEFAULT_LOCALE, extractLanguage, supportedLocaleMap } from './lang';

const getSupportedLocaleFromData = data =>
    flow(
        locale => (has(data, locale) ? locale : extractLanguage(locale)),
        locale => (has(data, locale) ? locale : DEFAULT_LOCALE)
    );

const getMessages = ({ allMessages }, locale) => allMessages[supportedLocaleMap[locale]] || allMessages[DEFAULT_LOCALE];

const isLangOnly = locale => !/-/.test(locale);

const defaultFor = locale => supportedLocaleMap[locale];

const patchMissingMessages = (knownMessages, messages) => {
    const result = cloneDeep(messages);

    flow(keys, messageKeys =>
        forEach(messageKeys, key => {
            if (!has(result, key)) {
                result[key] = knownMessages[key];
            }
        })
    )(knownMessages);

    return result;
};

const applyLocale = (state, preferredLocale) => {
    const { allMessages } = state;

    const getDisplayLocale = getSupportedLocaleFromData(allMessages);
    const getSupported = getSupportedLocaleFromData(supportedLocaleMap);

    const displayLocale = getDisplayLocale(preferredLocale);

    const closest = getSupported(preferredLocale);
    const supportedLocale = isLangOnly(closest) ? defaultFor(closest) : closest;

    const canFetchSupportedLocale = displayLocale !== supportedLocale;

    const displayMessages = patchMissingMessages(getMessages(state, DEFAULT_LOCALE), getMessages(state, displayLocale));

    return {
        allMessages,
        canFetchSupportedLocale,
        displayLocale,
        displayMessages,
        preferredLocale,
        supportedLocale,
    };
};

export const createDefaultState = () => {
    const localeOfUnknownUser = getDisplayLocale();

    const defaultMessages = {
        [DEFAULT_LOCALE]: messagesEN,
    };

    const allMessages = {
        ...defaultMessages,
        ...mergeLanguageData(defaultMessages, localeData[localeOfUnknownUser], localeOfUnknownUser),
    };
    return applyLocale({ allMessages }, localeOfUnknownUser);
};

const isDefaultLocaleForLang = locale => supportedLocaleMap[locale] === locale;

const hasLocale = locale => Boolean(supportedLocaleMap[locale]);

const mergeLanguageData = (allMessages, languageData, locale) => {
    const baseLang = extractLanguage(locale);
    const messages = {
        [locale]: languageData,
    };

    if (isDefaultLocaleForLang(locale) || !hasLocale(baseLang)) {
        messages[baseLang] = languageData;
    }

    return {
        ...allMessages,
        ...messages,
    };
};

export default function langReducer(state = createDefaultState()) {
    return state;
}
